import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import API from "../api/axios";
import "../styles/auth.css";

const LANGUAGES = [
  { code: "english", native: "English", flag: "🇬🇧" },
  { code: "hindi", native: "हिंदी", flag: "🇮🇳" },
  { code: "telugu", native: "తెలుగు", flag: "🇮🇳" },
  { code: "tamil", native: "தமிழ்", flag: "🇮🇳" },
  { code: "malayalam", native: "മലയാളം", flag: "🇮🇳" },
];

const styles = `
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  .vs-auth-step-pill{display:flex;justify-content:center;gap:8px;margin-bottom:24px}
  .vs-step-dot{width:28px;height:4px;border-radius:4px;background:var(--accent-tint);transition:background .3s}
  .vs-step-dot.active{background:var(--accent)}
  .vs-auth-step-label{font-size:11px;font-weight:700;color:var(--accent);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:16px}
  .vs-auth-back-btn{width:100%;padding:11px;background:transparent;border:1.5px solid var(--border);border-radius:12px;color:var(--text-3);font-family:var(--font-body);font-size:13px;cursor:pointer;transition:all .2s;margin-bottom:16px}
  .vs-auth-back-btn:hover{border-color:var(--border-strong);color:var(--text-2)}
  .vs-lang-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:20px}
  .vs-lang-btn{padding:12px 16px;background:var(--surface-2);border:1.5px solid var(--border);border-radius:12px;color:var(--text-2);font-family:var(--font-body);font-size:14px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:12px;transition:all .2s}
  .vs-lang-btn:hover{border-color:var(--border-strong);background:var(--accent-tint);color:var(--text)}
  .vs-lang-btn.active{border-color:var(--accent);background:var(--accent-tint);color:var(--text);font-weight:600}
  .vs-lang-check{margin-left:auto;color:var(--accent);font-weight:700}
  .vs-auth-card{max-width:480px}
`;

function Register() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedLang, setSelectedLang] = useState("english");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = styles;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const handleNext = () => {
    if (!name.trim() || !email.trim() || !password) { toast.error("Please fill in all fields"); return; }
    setStep(2);
  };

  const handleRegister = async () => {
    try {
      setLoading(true);
      await API.post("/auth/register", { name, email, password });
      // Save language choice permanently during signup
      i18n.changeLanguage(selectedLang);
      localStorage.setItem("language", selectedLang);
      toast.success("Account created! Check your email for OTP.");
      navigate("/verify-otp", { state: { email } });
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed");
      setStep(1);
    } finally { setLoading(false); }
  };

  return (
    <div className="vs-auth-page">
      <div className="vs-auth-card">
        <h1 className="vs-auth-logo">VaultSphere</h1>
        <p className="vs-auth-tagline">{step === 1 ? t("createAccount") : t("chooseLanguage")}</p>

        <div className="vs-auth-step-pill">
          <div className={`vs-step-dot${step >= 1 ? " active" : ""}`} />
          <div className={`vs-step-dot${step >= 2 ? " active" : ""}`} />
        </div>

        <div className="vs-auth-divider">
          <div className="vs-auth-divider-line" /><div className="vs-auth-divider-dot" /><div className="vs-auth-divider-line" />
        </div>

        {step === 1 ? (
          <>
            <p className="vs-auth-step-label">{t("reg_step1")}</p>
            <div className="vs-auth-fields">
              <input className="vs-auth-input" type="text" placeholder={t("fullName")} value={name} onChange={e => setName(e.target.value)} />
              <input className="vs-auth-input" type="email" placeholder={t("email")} value={email} onChange={e => setEmail(e.target.value)} />
              <input className="vs-auth-input" type="password" placeholder={t("password")} value={password} onChange={e => setPassword(e.target.value)} onKeyPress={e => e.key === "Enter" && handleNext()} />
            </div>
            <p className="vs-auth-hint">{t("passwordHint")}</p>
            <button className="vs-auth-btn" onClick={handleNext}>{t("reg_next")}</button>
          </>
        ) : (
          <>
            <p className="vs-auth-step-label">{t("reg_step2")}</p>
            <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 16 }}>{t("reg_pickOnce")}</p>
            <div className="vs-lang-grid">
              {LANGUAGES.map(l => (
                <button key={l.code} className={`vs-lang-btn${selectedLang === l.code ? " active" : ""}`} onClick={() => setSelectedLang(l.code)}>
                  <span style={{ fontSize: 18 }}>{l.flag}</span>
                  <span>{l.native}</span>
                  {selectedLang === l.code && <span className="vs-lang-check">✓</span>}
                </button>
              ))}
            </div>
            <button className="vs-auth-btn" onClick={handleRegister} disabled={loading}>
              {loading ? t("reg_creating") : t("registerButton")}
            </button>
            <button className="vs-auth-back-btn" onClick={() => setStep(1)}>{t("reg_back")}</button>
          </>
        )}

        <p className="vs-auth-footer">{t("alreadyAccount")} <Link to="/">{t("loginHere")}</Link></p>
      </div>
    </div>
  );
}

export default Register;